import {
  Box,
  Stack,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Virtuoso } from "react-virtuoso";
import { useSettingsContext } from "../SettingsProvider";
import { SettingsPageHeader, FloatingSaveButton } from "../components";
import JsonEditor from "@/components/JsonEditor";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CodeIcon from "@mui/icons-material/Code";

export interface ComplaintCodeNode {
  id: string;
  name: string;
  children?: ComplaintCodeNode[];
}

type ComplaintCodesSettings = {
  complaintCodes: ComplaintCodeNode[];
};

const ComplaintCodesSettings = () => {
  const { settings, setSettings } = useSettingsContext();

  // Memoize original values
  const originalSettings = useMemo(() => {
    const complaintCodes = (settings as any)?.complaintCodes;
    return {
      complaintCodes: complaintCodes ?? [],
    } as ComplaintCodesSettings;
  }, [settings]);

  // Local state
  const [complaintCodes, setComplaintCodes] = useState<ComplaintCodeNode[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const focusedFieldRef = React.useRef<{ path: string; field: 'id' | 'name' } | null>(null);
  
  // JSON Editor state
  const [jsonEditorOpen, setJsonEditorOpen] = useState<boolean>(false);
  const [jsonText, setJsonText] = useState<string>("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Initialize local state when settings change
  useEffect(() => {
    setComplaintCodes(originalSettings.complaintCodes);
    setIsInitialized(true);
  }, [originalSettings]);

  // Calculate if data is dirty
  const isDirty = useMemo(() => {
    if (!isInitialized) {
      return false;
    }
    return JSON.stringify(complaintCodes) !== JSON.stringify(originalSettings.complaintCodes);
  }, [complaintCodes, originalSettings, isInitialized]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await setSettings({
        complaintCodes,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to find a node in the tree by path
  const findNodeByPath = (
    nodes: ComplaintCodeNode[],
    path: number[]
  ): { node: ComplaintCodeNode; parent: ComplaintCodeNode[]; index: number } | null => {
    if (path.length === 0) return null;
    
    const [firstIndex, ...rest] = path;
    if (firstIndex < 0 || firstIndex >= nodes.length) return null;
    
    const node = nodes[firstIndex];
    if (rest.length === 0) {
      return { node, parent: nodes, index: firstIndex };
    }
    
    if (!node.children) return null;
    return findNodeByPath(node.children, rest);
  };

  // Helper function to update a node in the tree
  const updateNode = React.useCallback((path: number[], updates: Partial<ComplaintCodeNode>, field?: 'id' | 'name') => {
    const result = findNodeByPath(complaintCodes, path);
    if (!result) return;
    
    // Store focus info if this is a field update
    if (field) {
      const pathKey = path.join("-");
      focusedFieldRef.current = { path: pathKey, field };
    }
    
    const newNodes = [...complaintCodes];
    const updateRecursive = (nodes: ComplaintCodeNode[], [firstIndex, ...rest]: number[]): ComplaintCodeNode[] => {
      if (rest.length === 0) {
        return nodes.map((node, index) =>
          index === firstIndex ? { ...node, ...updates } : node
        );
      }
      return nodes.map((node, index) =>
        index === firstIndex
          ? { ...node, children: node.children ? updateRecursive(node.children, rest) : undefined }
          : node
      );
    };
    
    setComplaintCodes(updateRecursive(newNodes, path));
  }, [complaintCodes]);

  // Helper function to delete a node
  const deleteNode = (path: number[]) => {
    const newNodes = [...complaintCodes];
    const deleteRecursive = (nodes: ComplaintCodeNode[], [firstIndex, ...rest]: number[]): ComplaintCodeNode[] => {
      if (rest.length === 0) {
        return nodes.filter((_, index) => index !== firstIndex);
      }
      return nodes.map((node, index) =>
        index === firstIndex
          ? { ...node, children: node.children ? deleteRecursive(node.children, rest) : undefined }
          : node
      );
    };
    
    setComplaintCodes(deleteRecursive(newNodes, path));
    // Remove from expanded nodes if it was expanded
    const nodeId = findNodeByPath(complaintCodes, path)?.node.id;
    if (nodeId) {
      setExpandedNodes((prev) => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
    }
  };

  // Helper function to add a child node
  const addChildNode = (path: number[]) => {
    const newChild: ComplaintCodeNode = {
      id: "",
      name: "",
    };
    
    const newNodes = [...complaintCodes];
    const addRecursive = (nodes: ComplaintCodeNode[], [firstIndex, ...rest]: number[]): ComplaintCodeNode[] => {
      if (rest.length === 0) {
        return nodes.map((node, index) =>
          index === firstIndex
            ? { ...node, children: [...(node.children || []), newChild] }
            : node
        );
      }
      return nodes.map((node, index) =>
        index === firstIndex
          ? { ...node, children: node.children ? addRecursive(node.children, rest) : [newChild] }
          : node
      );
    };
    
    setComplaintCodes(addRecursive(newNodes, path));
    // Expand the parent node
    const parentNode = findNodeByPath(complaintCodes, path);
    if (parentNode) {
      setExpandedNodes((prev) => new Set(prev).add(parentNode.node.id));
    }
  };

  // Helper function to add a sibling node
  const addSiblingNode = (path: number[]) => {
    const newSibling: ComplaintCodeNode = {
      id: "",
      name: "",
    };
    
    const newNodes = [...complaintCodes];
    const addRecursive = (nodes: ComplaintCodeNode[], [firstIndex, ...rest]: number[]): ComplaintCodeNode[] => {
      if (rest.length === 0) {
        const result = [...nodes];
        result.splice(firstIndex + 1, 0, newSibling);
        return result;
      }
      return nodes.map((node, index) =>
        index === firstIndex
          ? { ...node, children: node.children ? addRecursive(node.children, rest) : undefined }
          : node
      );
    };
    
    setComplaintCodes(addRecursive(newNodes, path));
  };

  // Helper function to move a node up
  const moveNodeUp = (path: number[]) => {
    if (path[path.length - 1] === 0) return; // Already at top
    
    const newNodes = [...complaintCodes];
    const moveRecursive = (nodes: ComplaintCodeNode[], [firstIndex, ...rest]: number[]): ComplaintCodeNode[] => {
      if (rest.length === 0) {
        if (firstIndex === 0) return nodes;
        const newNodes = [...nodes];
        [newNodes[firstIndex - 1], newNodes[firstIndex]] = [newNodes[firstIndex], newNodes[firstIndex - 1]];
        return newNodes;
      }
      return nodes.map((node, index) =>
        index === firstIndex
          ? { ...node, children: node.children ? moveRecursive(node.children, rest) : undefined }
          : node
      );
    };
    
    setComplaintCodes(moveRecursive(newNodes, path));
  };

  // Helper function to move a node down
  const moveNodeDown = (path: number[]) => {
    const newNodes = [...complaintCodes];
    const getSiblingCount = (nodes: ComplaintCodeNode[], [firstIndex, ...rest]: number[]): number => {
      if (rest.length === 0) return nodes.length;
      const node = nodes[firstIndex];
      if (!node.children) return 0;
      return getSiblingCount(node.children, rest);
    };
    
    const siblingCount = getSiblingCount(newNodes, path);
    if (path[path.length - 1] >= siblingCount - 1) return; // Already at bottom
    
    const moveRecursive = (nodes: ComplaintCodeNode[], [firstIndex, ...rest]: number[]): ComplaintCodeNode[] => {
      if (rest.length === 0) {
        if (firstIndex >= nodes.length - 1) return nodes;
        const newNodes = [...nodes];
        [newNodes[firstIndex], newNodes[firstIndex + 1]] = [newNodes[firstIndex + 1], newNodes[firstIndex]];
        return newNodes;
      }
      return nodes.map((node, index) =>
        index === firstIndex
          ? { ...node, children: node.children ? moveRecursive(node.children, rest) : undefined }
          : node
      );
    };
    
    setComplaintCodes(moveRecursive(newNodes, path));
  };

  // Toggle expanded state
  const toggleExpanded = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // Flatten tree to visible nodes based on expanded state
  interface FlattenedNode {
    node: ComplaintCodeNode;
    path: number[];
    level: number;
    nodeId: string;
    pathKey: string;
    hasChildren: boolean;
    isExpanded: boolean;
    siblingCount: number;
  }

  const flattenedNodes = useMemo(() => {
    const result: FlattenedNode[] = [];
    
    const flattenRecursive = (
      nodes: ComplaintCodeNode[],
      path: number[],
      level: number
    ) => {
      nodes.forEach((node, index) => {
        const currentPath = [...path, index];
        const nodeId = node.id || `node-${currentPath.join("-")}`;
        const pathKey = `node-${currentPath.join("-")}`;
        const isExpanded = expandedNodes.has(nodeId);
        const hasChildren = node.children && node.children.length > 0;
        // Sibling count is just the length of the current nodes array
        const siblingCount = nodes.length;

        result.push({
          node,
          path: currentPath,
          level,
          nodeId,
          pathKey,
          hasChildren: hasChildren || false,
          isExpanded,
          siblingCount,
        });

        // If expanded and has children, recursively add children
        if (isExpanded && hasChildren && node.children) {
          flattenRecursive(node.children, currentPath, level + 1);
        }
      });
    };

    flattenRecursive(complaintCodes, [], 0);
    return result;
  }, [complaintCodes, expandedNodes]);

  // Render a single node (for virtualization)
  const renderNodeItem = useCallback((index: number) => {
    const item = flattenedNodes[index];
    if (!item) return null;

    const { node, path, level, nodeId, pathKey, hasChildren, isExpanded, siblingCount } = item;

    // Calculate indentation - each level adds 32px (2rem) for better visibility
    const indentPx = level * 32;

    return (
      <Box 
        key={pathKey} 
        sx={{ 
          mb: 1, 
          pr: 1,
          pl: `${indentPx + 16}px`, // Base padding + level-based indentation
          position: 'relative',
          // Add a vertical line to show hierarchy
          '&::before': level > 0 ? {
            content: '""',
            position: 'absolute',
            left: `${indentPx - 8}px`,
            top: 0,
            bottom: 0,
            width: '2px',
            backgroundColor: 'divider',
            opacity: 0.5,
          } : {},
          // Add a horizontal connector line
          '&::after': level > 0 ? {
            content: '""',
            position: 'absolute',
            left: `${indentPx - 8}px`,
            top: '24px',
            width: '16px',
            height: '2px',
            backgroundColor: 'divider',
            opacity: 0.5,
          } : {},
        }}
      >
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                {/* Expand/Collapse button */}
                {hasChildren && (
                  <IconButton
                    size="small"
                    onClick={() => toggleExpanded(nodeId)}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                )}
                {!hasChildren && <Box sx={{ width: 40 }} />}

                {/* ID Field */}
                <TextField
                  inputRef={(input) => {
                    if (focusedFieldRef.current?.path === pathKey && focusedFieldRef.current?.field === 'id' && input) {
                      setTimeout(() => {
                        input.focus();
                        focusedFieldRef.current = null;
                      }, 0);
                    }
                  }}
                  label="ID"
                  value={node.id}
                  onChange={(e) => updateNode(path, { id: e.target.value }, 'id')}
                  size="small"
                  sx={{ width: 150 }}
                  placeholder="e.g., J0000"
                />

                {/* Name Field */}
                <TextField
                  inputRef={(input) => {
                    if (focusedFieldRef.current?.path === pathKey && focusedFieldRef.current?.field === 'name' && input) {
                      setTimeout(() => {
                        input.focus();
                        focusedFieldRef.current = null;
                      }, 0);
                    }
                  }}
                  label="Name"
                  value={node.name}
                  onChange={(e) => updateNode(path, { name: e.target.value }, 'name')}
                  size="small"
                  sx={{ flex: 1 }}
                  placeholder="e.g., INJURY"
                />

                {/* Action Buttons */}
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveNodeUp(path)}
                        disabled={path[path.length - 1] === 0}
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveNodeDown(path)}
                        disabled={path[path.length - 1] >= siblingCount - 1}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Add child">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => addChildNode(path)}
                        color="primary"
                      >
                        <AddIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Add sibling">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => addSiblingNode(path)}
                        color="primary"
                      >
                        <PlaylistAddIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => deleteNode(path)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }, [flattenedNodes, toggleExpanded, updateNode, moveNodeUp, moveNodeDown, addChildNode, addSiblingNode, deleteNode]);

  // Render a node recursively (kept for reference, but not used with virtualization)
  const renderNode = (node: ComplaintCodeNode, path: number[], level: number = 0) => {
    const nodeId = node.id || `node-${path.join("-")}`;
    const pathKey = `node-${path.join("-")}`;
    const isExpanded = expandedNodes.has(nodeId);
    const hasChildren = node.children && node.children.length > 0;
    const isRoot = level === 0;
    const siblingCount = isRoot 
      ? complaintCodes.length 
      : findNodeByPath(complaintCodes, path.slice(0, -1))?.node.children?.length || 0;

    return (
      <Box key={pathKey} sx={{ mb: 1 }}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                {/* Expand/Collapse button */}
                {hasChildren && (
                  <IconButton
                    size="small"
                    onClick={() => toggleExpanded(nodeId)}
                    sx={{ ml: level * 2 }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                )}
                {!hasChildren && <Box sx={{ width: 40, ml: level * 2 }} />}

                {/* ID Field */}
                <TextField
                  inputRef={(input) => {
                    if (focusedFieldRef.current?.path === pathKey && focusedFieldRef.current?.field === 'id' && input) {
                      setTimeout(() => {
                        input.focus();
                        focusedFieldRef.current = null;
                      }, 0);
                    }
                  }}
                  label="ID"
                  value={node.id}
                  onChange={(e) => updateNode(path, { id: e.target.value }, 'id')}
                  size="small"
                  sx={{ width: 150 }}
                  placeholder="e.g., J0000"
                />

                {/* Name Field */}
                <TextField
                  inputRef={(input) => {
                    if (focusedFieldRef.current?.path === pathKey && focusedFieldRef.current?.field === 'name' && input) {
                      setTimeout(() => {
                        input.focus();
                        focusedFieldRef.current = null;
                      }, 0);
                    }
                  }}
                  label="Name"
                  value={node.name}
                  onChange={(e) => updateNode(path, { name: e.target.value }, 'name')}
                  size="small"
                  sx={{ flex: 1 }}
                  placeholder="e.g., INJURY"
                />

                {/* Action Buttons */}
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveNodeUp(path)}
                        disabled={path[path.length - 1] === 0}
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveNodeDown(path)}
                        disabled={path[path.length - 1] >= siblingCount - 1}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Add child">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => addChildNode(path)}
                        color="primary"
                      >
                        <AddIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Add sibling">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => addSiblingNode(path)}
                        color="primary"
                      >
                        <PlaylistAddIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => deleteNode(path)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              {/* Children */}
              {hasChildren && (
                <Collapse in={isExpanded}>
                  <Box sx={{ pl: 4 }}>
                    <Stack spacing={1}>
                      {node.children!.map((child, index) =>
                        renderNode(child, [...path, index], level + 1)
                      )}
                    </Stack>
                  </Box>
                </Collapse>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const addRootNode = () => {
    const newRoot: ComplaintCodeNode = {
      id: "",
      name: "",
    };
    setComplaintCodes([...complaintCodes, newRoot]);
  };

  // JSON Editor handlers
  const handleOpenJsonEditor = () => {
    const jsonString = JSON.stringify(complaintCodes, null, 2);
    setJsonText(jsonString);
    setJsonError(null);
    setJsonEditorOpen(true);
  };

  const handleCloseJsonEditor = () => {
    setJsonEditorOpen(false);
    setJsonText("");
    setJsonError(null);
  };

  const handleJsonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonText(event.target.value);
    setJsonError(null);
  };

  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      
      // Validate the structure
      if (!Array.isArray(parsed)) {
        setJsonError("JSON must be an array of complaint code nodes");
        return;
      }

      // Validate each node has required fields
      const validateNode = (node: any, path: string = "root"): string | null => {
        if (!node || typeof node !== "object") {
          return `${path}: Node must be an object`;
        }
        if (typeof node.id !== "string") {
          return `${path}: Node must have an 'id' field (string)`;
        }
        if (typeof node.name !== "string") {
          return `${path}: Node must have a 'name' field (string)`;
        }
        if (node.children !== undefined) {
          if (!Array.isArray(node.children)) {
            return `${path}: 'children' must be an array if present`;
          }
          for (let i = 0; i < node.children.length; i++) {
            const childError = validateNode(node.children[i], `${path}[${i}]`);
            if (childError) return childError;
          }
        }
        return null;
      };

      for (let i = 0; i < parsed.length; i++) {
        const error = validateNode(parsed[i], `[${i}]`);
        if (error) {
          setJsonError(error);
          return;
        }
      }

      // Apply the changes
      setComplaintCodes(parsed as ComplaintCodeNode[]);
      handleCloseJsonEditor();
    } catch (error: any) {
      setJsonError(error.message || "Invalid JSON format");
    }
  };

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3, pb: 10 }}>
        <Stack spacing={4} sx={{ width: "100%" }}>
          {/* Header with dirty indicator */}
          <SettingsPageHeader title="Complaint Codes Settings" isDirty={isDirty} />

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CodeIcon />}
              onClick={handleOpenJsonEditor}
            >
              Edit JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addRootNode}
            >
              Add Root Category
            </Button>
          </Box>

          {/* Complaint Codes Tree - Virtualized */}
          {complaintCodes.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 4 }}
            >
              No complaint codes configured. Click "Add Root Category" to get started.
            </Typography>
          ) : (
            <Box sx={{ height: "calc(100vh - 300px)", minHeight: 400 }}>
              <Virtuoso
                totalCount={flattenedNodes.length}
                itemContent={renderNodeItem}
                style={{ height: "100%" }}
              />
            </Box>
          )}
        </Stack>

        {/* Floating Save Button */}
        <FloatingSaveButton show={isDirty} onSave={handleSave} isSaving={isSaving} />
      </Box>

      {/* JSON Editor Dialog */}
      <Dialog
        open={jsonEditorOpen}
        onClose={handleCloseJsonEditor}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h5">Edit Complaint Codes JSON</Typography>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", p: 2 }}>
          {jsonError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {jsonError}
            </Alert>
          )}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <JsonEditor
              value={jsonText}
              onChange={handleJsonChange}
              label="Complaint Codes JSON"
              height="calc(90vh - 200px)"
              error={!!jsonError}
              helperText={jsonError || "Modify the JSON structure and click 'Apply' to update the tree"}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseJsonEditor} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleApplyJson}
            variant="contained"
            color="primary"
            disabled={!!jsonError}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export { ComplaintCodesSettings };

